<template>
	<div>
		<div
			v-if="loading"
			class="d-flex align-items-center gap-2 small text-body-secondary"
		>
			<span
				class="spinner-border spinner-border-sm"
				role="status"
				aria-hidden="true"
			></span>
			Loading station #{{ stationId }}…
		</div>

		<div
			v-else-if="error"
			class="alert alert-danger py-2 small mb-0"
			role="alert"
		>
			{{ error }}
		</div>

		<div v-else-if="detail">
			<div class="d-flex flex-wrap align-items-center gap-2 mb-2">
				<span
					class="badge rounded-pill"
					:class="detail.active ? 'bg-success' : 'bg-secondary'"
				>
					{{ detail.active ? "Active" : "Retired" }}
				</span>
				<span
					class="badge rounded-pill"
					:class="
						detail.permission === 'permitted'
							? 'bg-success'
							: 'bg-warning text-dark'
					"
				>
					{{ PERMISSION_LABELS[detail.permission] }}
				</span>
				<RouterLink
					:to="`/_/traps/stations/${detail.id}`"
					class="btn btn-sm btn-outline-secondary ms-auto"
				>
					<i class="bi bi-box-arrow-up-right me-1"></i>Open full station
				</RouterLink>
				<button
					type="button"
					class="btn btn-sm btn-primary"
					@click="emit('set-trap', detail.site_id)"
				>
					<i class="bi bi-plus-lg me-1"></i>Set trap here
				</button>
			</div>

			<!-- An imported station states no creator, a station whose site is
			     not parcel-linked states no parcel, and a station with no
			     recorded location states none — each says so rather than
			     leaving a blank the operator could read as zero. -->
			<dl class="row row-cols-1 row-cols-md-2 g-2 mb-0 small">
				<div class="col">
					<dt class="text-body-secondary fw-normal">Site</dt>
					<dd class="mb-0">{{ detail.site_address || "—" }}</dd>
				</div>
				<div class="col">
					<dt class="text-body-secondary fw-normal">Site kind</dt>
					<dd class="mb-0">{{ detail.site_kind || "Not recorded" }}</dd>
				</div>
				<div class="col">
					<dt class="text-body-secondary fw-normal">Parcel</dt>
					<dd class="mb-0">
						<template v-if="detail.parcel">
							<div>{{ parcelTitle }}</div>
							<div class="text-body-secondary">
								{{ detail.parcel.property_type || "—" }}
							</div>
							<div class="text-body-secondary">
								Owner: {{ detail.parcel.owner || "—" }}
							</div>
						</template>
						<template v-else>Not recorded</template>
					</dd>
				</div>
				<div class="col">
					<dt class="text-body-secondary fw-normal">Location</dt>
					<dd class="mb-0">
						<template v-if="detail.location">
							<div>
								{{ detail.location.latitude }}, {{ detail.location.longitude }}
							</div>
							<div class="text-body-secondary">
								Recorded
								{{ formatDate(new Date(detail.location.recorded_at)) }}
							</div>
						</template>
						<template v-else>Not recorded</template>
					</dd>
				</div>
				<div class="col">
					<dt class="text-body-secondary fw-normal">Created</dt>
					<dd class="mb-0">{{ formatDate(new Date(detail.created)) }}</dd>
				</div>
				<div class="col">
					<dt class="text-body-secondary fw-normal">Created by</dt>
					<dd class="mb-0">{{ creatorText }}</dd>
				</div>
			</dl>

			<h3 class="h6 mt-3">
				Trap instances here ({{ orderedInstances.length }})
			</h3>
			<p
				v-if="orderedInstances.length === 0"
				class="small text-body-secondary mb-0"
			>
				No trap has been set at this station.
			</p>
			<div v-else class="list-group list-group-flush">
				<button
					v-for="instance in orderedInstances"
					:key="instance.id"
					type="button"
					class="list-group-item list-group-item-action py-1 px-1"
					@click="emit('select', { kind: 'trap', id: instance.id })"
				>
					<div class="d-flex justify-content-between align-items-center gap-2">
						<span class="text-truncate fw-semibold">
							#{{ instance.id }} · {{ formatDate(new Date(instance.date)) }}
						</span>
						<span
							class="badge rounded-pill"
							:class="
								instance.collected_at === null
									? 'bg-warning text-dark'
									: 'bg-secondary'
							"
						>
							{{ INSTANCE_STATE_LABELS[instance.state] ?? instance.state }}
						</span>
					</div>
					<div class="small text-body-secondary text-truncate">
						{{ instance.site || `Site #${instance.site_id}` }}
						<template v-if="instance.trap_type">
							· {{ instance.trap_type }}
						</template>
					</div>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { RouterLink } from "vue-router";

import { apiClient } from "@/client";
import { INSTANCE_STATE_LABELS } from "@/components/map-review/columns";
import { formatDate } from "@/format";
import type { TrapStationDetail } from "@/type/api";

import type { Selection } from "./model";
import { describeError } from "./wire";

const props = defineProps<{ stationId: number }>();

const emit = defineEmits<{
	select: [selection: Selection];
	notify: [title: string, message: string];
	"set-trap": [siteId: number];
}>();

const PERMISSION_LABELS: Record<string, string> = {
	permitted: "Permitted",
	awaiting_permission: "Awaiting permission",
};

const detail = ref<TrapStationDetail | null>(null);
const loading = ref(false);
const error = ref("");

const creatorText = computed(() => {
	const row = detail.value;
	if (!row) return "";
	if (row.created_by_name) return row.created_by_name;
	return row.created_by === null ? "Not recorded" : `User #${row.created_by}`;
});

/** The county identity a parcel is keyed on. Both halves are stated on every
 * imported parcel, but a blank one is still printed as missing rather than as
 * a stray space. */
const parcelTitle = computed(() => {
	const parcel = detail.value?.parcel;
	if (!parcel) return "Not recorded";
	return [parcel.region, parcel.external_id].filter(Boolean).join(" ") || "—";
});

/** Newest first: the instance an operator opened this station for is almost
 * always its most recent one — usually the one still out. */
const orderedInstances = computed(() =>
	[...(detail.value?.instances ?? [])].sort(
		(left, right) =>
			new Date(right.date).getTime() - new Date(left.date).getTime(),
	),
);

let readToken = 0;

// The sheet holds the station's small reading; the page it links to is the
// full review. The read is per selection, so a station the district no longer
// returns (another org's id, or a deleted row) is stated rather than left as
// the previous station's facts.
async function load() {
	const token = ++readToken;
	detail.value = null;
	error.value = "";
	loading.value = true;
	try {
		const row = await apiClient.JSONGet<TrapStationDetail>(
			`/api/trap-station/${props.stationId}`,
		);
		if (token !== readToken) return;
		detail.value = row;
	} catch (e: unknown) {
		if (token !== readToken) return;
		error.value = describeError(e);
		emit("notify", "Station not read", error.value);
	} finally {
		if (token === readToken) loading.value = false;
	}
}

watch(() => props.stationId, load, { immediate: true });
</script>
